import { useState, useEffect, useContext } from 'react'
import { Box, Checkbox, Grid, ListItem, TextField, Typography } from '@mui/material'
import { Autocomplete } from '@mui/material'
import { Node as NodeIcon } from 'genesis-suite/icons'
import { SwalContext } from 'genesis-suite/components'
import { modelService } from '../../lib/services'
import { lowerCaseFirstLetter } from '../../lib/utils'
import { useDebounce } from 'use-debounce'

const generateValueOptions = (data, propName) => {
    const keyPropName = lowerCaseFirstLetter(propName)
    // eslint-disable-next-line
    if (!data[0] || data[0][keyPropName] == undefined) return []
    return data.map(item => {
        const nameProp = Object.keys(item).find(key => key.toLowerCase().includes('name'))
        const label = nameProp ? `${item[keyPropName]} - ${item[nameProp]}` : item[keyPropName]
        return {
            label,
            value: item[keyPropName],
        }
    })
}

const ValueSelector = props => {
    const {
        id,
        context,
        allOptions,
        nodeOptions,
        propsByNodeId,
        defaultNode,
        defaultProperty,
        defaultValues,
        onChange,
    } = props

    const [node, setNode] = useState(nodeOptions.find(n => n.value === defaultNode))
    const [property, setProperty] = useState(
        propsByNodeId[defaultNode] && propsByNodeId[defaultNode].find(p => p.value === defaultProperty)
    )
    const [values, setValues] = useState(defaultValues)
    const [valueOptions, setValueOptions] = useState([])
    const [loading, setLoading] = useState(false)
    const { alert } = useContext(SwalContext)
    const [searchText, setSearchText] = useState('')
    const [debouncedSearchText] = useDebounce(searchText, 300)

    useEffect(() => {
        if (!node) return
        const { indexProperty } = allOptions.find(obj => obj.id === node.value)
        const indexPropName = indexProperty && indexProperty.name
        setLoading(true)
        let cancelCall = () => {}
        modelService
            .getResourceValues(
                context,
                node.label,
                { pageSize: 1000, pageIndex: 1, controlField: indexPropName, searchText: debouncedSearchText },
                '',
                cancel => {
                    cancelCall = cancel
                }
            )
            .then(result => {
                if (result.values) {
                    const valueOptions = indexPropName ? generateValueOptions(result.values, indexPropName) : []
                    setValueOptions(valueOptions)
                } else setValueOptions([])
            })
            .catch(err => {
                if (err.__CANCEL__) return
                alert(err.Message || err.message, { type: 'error' })
            })
            .finally(() => setLoading(false))

        return () => cancelCall()
    }, [node, debouncedSearchText])

    const handleNodeChange = option => {
        setNode(option)
        const indexProperty = propsByNodeId[option.value].find(prop => prop.key)
        setProperty(indexProperty)
        setValueOptions([])
        setValues([])
        onChange(option, id, 'node', indexProperty.value)
    }

    const handleValueChange = options => {
        setSearchText('')
        setValues(options)
        onChange(options, id, 'values')
    }

    return (
        <Grid container spacing={8}>
            <Grid item xs={4}>
                <Autocomplete
                    disableClearable
                    ChipProps={{ size: 'small' }}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={params => <TextField placeholder="Select Node..." {...params} />}
                    renderOption={(props, option) => (
                        <ListItem {...props}>
                            {option.type === 'node' && <NodeIcon fontSize="small" />}
                            <Box ml={1} />
                            <Typography>{option.label}</Typography>
                        </ListItem>
                    )}
                    options={nodeOptions}
                    value={node ?? ''}
                    onChange={(e, v) => handleNodeChange(v)}
                />
            </Grid>
            <Grid item xs={4}>
                <Autocomplete
                    disabled
                    renderInput={params => <TextField placeholder="Select a Node." {...params} />}
                    ChipProps={{ size: 'small' }}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    getOptionLabel={option => option.label ?? ''}
                    options={[]}
                    value={property ?? ''}
                />
            </Grid>
            <Grid item xs={4}>
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    // filterSelectedOptions
                    ChipProps={{ size: 'small' }}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    getOptionLabel={option => option.label ?? ''}
                    renderInput={params => <TextField placeholder="Select Value(s)..." {...params} />}
                    options={valueOptions}
                    value={values ?? []}
                    onChange={(e, v) => handleValueChange(v)}
                    loading={loading}
                    inputValue={searchText}
                    renderOption={(props, option, { selected }) => {
                        const { key, ...optionProps } = props
                        return (
                            <li key={key} {...optionProps}>
                                <Checkbox checked={selected} />
                                {option.label}
                            </li>
                        )
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                        if (reason === 'reset') return
                        setSearchText(newInputValue)
                    }}
                />
            </Grid>
        </Grid>
    )
}

export default ValueSelector
